import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import './select-invoice-page.scss'; // Assuming the styling is similar to InvoicePage

const SelectInvoicePage = () => {
    const [invoices, setInvoices] = useState([]);
    const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
    const [invoiceBreakdown, setInvoiceBreakdown] = useState([]);
    const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [emailAddress, setEmailAddress] = useState('');
    const [invoiceHtml, setInvoiceHtml] = useState('');
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
    
    // New payment form states
    const [clients, setClients] = useState([]);
    const [selectedClientName, setSelectedClientName] = useState('');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    
    // Confirmation dialog states
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [pendingInvoicesUpdate, setPendingInvoicesUpdate] = useState(null);

    // Timeout handling states
    const [isLoading, setIsLoading] = useState(false);
    const [timeoutError, setTimeoutError] = useState(null);

    // Payable Details modal states
    const [showPayableModal, setShowPayableModal] = useState(false);
    const [payableDetails, setPayableDetails] = useState([]);
    const [payableTotal, setPayableTotal] = useState(0);
    const [isLoadingPayable, setIsLoadingPayable] = useState(false);
    const [payableEmailAddress, setPayableEmailAddress] = useState('');
    const [isSendingPayableEmail, setIsSendingPayableEmail] = useState(false);
    const [closedInvoicesOpen, setClosedInvoicesOpen] = useState(false);

    // Business metrics states
    const [businessMetrics, setBusinessMetrics] = useState({
        ytdRevenue: 0,
        lastMonthRevenue: 0,
        ytdProfit: 0,
        lastMonthProfit: 0,
        ytdMargin: 0,
        totalPaymentOutstanding: 0,
        totalRevenueOutstanding: 0
    });

    const navigate = useNavigate();

    // Format dates to YYYY-MM-DD without timezone shift (backend returns datetime strings)
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const str = String(dateString);
        const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
        if (match) return match[1];
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // SOW invoices: READY → SENT → CLOSED (skip PAID)
    const isSowInvoice = (invoice) => {
        if (!invoice) return false;
        if (invoice.is_sow === true) return true;
        // Fallback: SOW bills have no timesheet invoice_link and no recruiter pay
        return !invoice.invoice_link && (invoice.pay_recruiter == null || Number(invoice.pay_recruiter) === 0);
    };

    const refreshBusinessMetrics = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const metricsResponse = await axios.get(
                `${process.env.REACT_APP_RYZ_SERVER}/get_business_metrics`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setBusinessMetrics(metricsResponse.data);
        } catch (err) {
            console.warn('Failed to refresh business metrics:', err);
        }
    };

    useEffect(() => {
        const fetchInvoices = async () => {
            setIsLoading(true);
            setTimeoutError(null);

            try {
                const token = sessionStorage.getItem('token');

                // Fetch main invoices and business metrics in parallel
                const [response, metricsResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_RYZ_SERVER}/list_client_invoices`, {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    }),
                    axios.get(`${process.env.REACT_APP_RYZ_SERVER}/get_business_metrics`, {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    }).catch(err => {
                        console.warn('Business metrics endpoint not available:', err);
                        return { data: {
                            ytdRevenue: 0,
                            lastMonthRevenue: 0,
                            ytdProfit: 0,
                            lastMonthProfit: 0,
                            ytdMargin: 0,
                            totalPaymentOutstanding: 0,
                            totalRevenueOutstanding: 0
                        }};
                    })
                ]);

                setInvoices(response.data);
                setBusinessMetrics(metricsResponse.data);

                // Get unique clients from invoices array after invoices are loaded
                //console.log('response.data', response.data);

                // Create a Map to ensure uniqueness by client_id or fallback to client_name
                const clientMap = new Map();

                response.data.forEach(invoice => {
                    // Create a unique key that combines client_id (if exists) with client_name
                    // This ensures we don't get duplicates even if client_id is missing
                    const clientKey = invoice.client_id ?
                        `id_${invoice.client_id}` :
                        `name_${invoice.client_name.replace(/[^a-zA-Z0-9]/g, '_')}`;

                    if (!clientMap.has(clientKey)) {
                        clientMap.set(clientKey, {
                            id: invoice.client_id || `name_${invoice.client_name}`,
                            name: invoice.client_name,
                            project: invoice.project_name || null
                        });
                    }
                });

                const uniqueClients = Array.from(clientMap.values());
                setClients(uniqueClients);
            } catch (error) {
                console.error('There was an error fetching the invoices!', error);
                setTimeoutError({
                    type: 'session_expired',
                    message: 'Your session has expired. Please log in again.'
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchInvoices();

        // Prevent swipe navigation
        const preventSwipeNavigation = (e) => {
            if (e.touches && e.touches.length > 1) return;
            
            // Prevent horizontal swipe gestures from triggering browser navigation
            const target = e.target.closest('.select-invoice-container');
            if (target && Math.abs(e.deltaX || 0) > Math.abs(e.deltaY || 0)) {
                e.preventDefault();
            }
        };

        const preventTouchMove = (e) => {
            // Only prevent if it's a horizontal swipe on our container
            if (e.target.closest('.select-invoice-container')) {
                const touch = e.touches[0];
                if (touch) {
                    // Allow vertical scrolling but prevent horizontal swiping
                    const element = e.target.closest('.select-invoice-container');
                    if (element && !element.contains(e.target.closest('.breakdown-table-wrapper'))) {
                        // This helps prevent accidental navigation
                        if (Math.abs(e.touches[0].clientX - (e.touches[0].pageX || 0)) > 10) {
                            e.preventDefault();
                        }
                    }
                }
            }
        };

        // Add event listeners
        document.addEventListener('touchstart', preventSwipeNavigation, { passive: false });
        document.addEventListener('touchmove', preventTouchMove, { passive: false });
        document.addEventListener('wheel', preventSwipeNavigation, { passive: false });

        return () => {
            document.removeEventListener('touchstart', preventSwipeNavigation);
            document.removeEventListener('touchmove', preventTouchMove);
            document.removeEventListener('wheel', preventSwipeNavigation);
        };
    }, []);

    const handleSendEmail = async (invoice) => {
        setSelectedInvoice(invoice);
        setIsLoadingInvoice(true);
        
        // Set default email to client's email if available
        setEmailAddress(invoice.client_email || '');
        
        try {
            // Fetch the invoice HTML
            const token = sessionStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_RYZ_SERVER}/get_invoice/${invoice.inv_hash}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setInvoiceHtml(response.data.html);
            setShowEmailModal(true);
        } catch (error) {
            console.error('Error fetching invoice HTML:', error);
            alert('Failed to load invoice. Please try again.');
        } finally {
            setIsLoadingInvoice(false);
        }
    };

    const handleCloseModal = () => {
        setShowEmailModal(false);
        setSelectedInvoice(null);
        setInvoiceHtml('');
    };

    const handleDeleteInvoice = async (invoice) => {
        const ok = window.confirm(
            `Delete invoice #${invoice.id} for ${invoice.client_name}?\n\n` +
            `This removes the client invoice from the system. ` +
            `Linked SOW installments (if any) will go back to Not Invoiced. ` +
            `Timesheet hour records are kept.`
        );
        if (!ok) return;

        setDeletingInvoiceId(invoice.id);
        try {
            const token = sessionStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_RYZ_SERVER}/delete_client_invoice/${invoice.id}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
            if (expandedInvoiceId === invoice.id) {
                setExpandedInvoiceId(null);
                setInvoiceBreakdown([]);
            }
            if (selectedInvoice?.id === invoice.id) {
                handleCloseModal();
            }
        } catch (error) {
            console.error('Error deleting invoice:', error);
            const detail = error.response?.data?.detail;
            alert(typeof detail === 'string' ? detail : 'Failed to delete invoice. Please try again.');
        } finally {
            setDeletingInvoiceId(null);
        }
    };

    const handleSendEmailSubmit = async () => {
        if (!emailAddress.trim()) {
            alert('Please enter an email address');
            return;
        }

        setIsSendingEmail(true);
        
        try {
            const emailPayload = {
                to_email: emailAddress,
                to_name: selectedInvoice.client_name,
                subject: `Invoice #${selectedInvoice.id} from Rayze`,
                content: invoiceHtml,
                from_email: process.env.REACT_APP_SENDMAIL_FROM || 'noreply@rayze.com',
                cc_email: process.env.REACT_APP_SENDMAIL_CC_CLIENT || 'jc@rayze.xyz'
            };

            // Check for test email override
            if (process.env.REACT_APP_SENDMAIL_TEST) {
                emailPayload.to_email = process.env.REACT_APP_SENDMAIL_TEST;
                console.log('test email done');
            }

            console.log('Email payload being sent:', {
                ...emailPayload,
                content: emailPayload.content ? `[HTML content - ${emailPayload.content.length} chars]` : 'No content'
            });

            const emailResponse = await axios.post(
                `${process.env.REACT_APP_RYZ_SENDMAIL}/send_html_email`,
                emailPayload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            console.log('Email sent successfully:', emailResponse.data);
            
            // Update invoice status to SENT after successful email send
            if (selectedInvoice.inv_status === 'READY') {
                try {
                    const token = sessionStorage.getItem('token');
                    await axios.put(
                        `${process.env.REACT_APP_RYZ_SERVER}/update_client_invoice/${selectedInvoice.id}`,
                        { inv_status: 'SENT' },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    
                    // Update local state
                    setInvoices(invoices.map(invoice => 
                        invoice.id === selectedInvoice.id 
                            ? { ...invoice, inv_status: 'SENT' }
                            : invoice
                    ));
                } catch (statusError) {
                    console.error('Error updating invoice status:', statusError);
                    // Don't show error to user since email was sent successfully
                }
            }
            
            alert('Invoice email sent successfully!');
            handleCloseModal();
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Failed to send email. Please try again.');
        } finally {
            setIsSendingEmail(false);
        }
    };


    const fetchInvoiceBreakdown = async (clientInvoice) => {
        setIsLoadingBreakdown(true);
        
        try {
            const token = sessionStorage.getItem('token');
            
            // Parse the comma-separated invoice IDs from invoice_link
            const invoiceIds = clientInvoice.invoice_link
                ? clientInvoice.invoice_link.split(',').map(id => id.trim()).filter(id => id)
                : [];
            
            if (invoiceIds.length === 0) {
                setInvoiceBreakdown([]);
                setIsLoadingBreakdown(false);
                return;
            }
            
            // Fetch breakdown details for each invoice ID
            const breakdownPromises = invoiceIds.map(invoiceId =>
                axios.get(`${process.env.REACT_APP_RYZ_SERVER}/invoice_breakdown/${invoiceId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                })
            );
            
            const responses = await Promise.all(breakdownPromises);
            const breakdownData = responses.map(response => ({
                ...response.data,
                clientInvoiceStatus: clientInvoice.inv_status // Add client invoice status to each breakdown item
            })).flat();
            
            setInvoiceBreakdown(breakdownData);
        } catch (error) {
            console.error('Error fetching invoice breakdown:', error);
            setInvoiceBreakdown([]);
        } finally {
            setIsLoadingBreakdown(false);
        }
    };

    const handleRowClick = (invoice) => {
        // Toggle the expanded state
        if (expandedInvoiceId === invoice.id) {
            // If clicking the same row, collapse it
            setExpandedInvoiceId(null);
            setInvoiceBreakdown([]);
        } else {
            // Expand this row and fetch breakdown data
            setExpandedInvoiceId(invoice.id);
            fetchInvoiceBreakdown(invoice);
        }
    };

    // Helper function to find subset sum combinations, prioritizing single rows first, then older entries
    const findSubsetSum = (arr, targetSum) => {
        const n = arr.length;
        const combinations = [];
        
        // First, check for single-row exact matches (arr is sorted oldest-first, so first match is oldest)
        for (let j = 0; j < n; j++) {
            if (Math.abs(arr[j].amount - targetSum) < 0.01) {
                return [[arr[j]]];
            }
        }
        
        // If no single row match found, look for multi-row combinations
        for (let i = 2; i < (1 << n); i++) {
            const combination = [];
            let sum = 0;
            
            for (let j = 0; j < n; j++) {
                if (i & (1 << j)) {
                    combination.push(arr[j]);
                    sum += arr[j].amount;
                }
            }
            
            if (Math.abs(sum - targetSum) < 0.01) {
                combinations.push(combination);
            }
        }
        
        // Sort: prefer fewer invoices, then prefer older entries (by oldest inv_date in combo)
        const getOldestDate = (combo) => {
            const dates = combo.map(x => x.invoice?.inv_date ? new Date(x.invoice.inv_date).getTime() : Infinity);
            return Math.min(...dates);
        };
        combinations.sort((a, b) => {
            if (a.length !== b.length) return a.length - b.length;
            return getOldestDate(a) - getOldestDate(b);
        });
        
        return combinations;
    };

    const handlePaymentAction = async (action) => {
        if (!selectedClientName) {
            alert('Please select a client');
            return;
        }
        
        if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        setIsProcessingPayment(true);
        
        try {
            const targetAmount = parseFloat(paymentAmount);
            
            // Filter invoices for the selected client
            const clientInvoices = invoices.filter(invoice =>
                invoice.client_name === selectedClientName &&
                (action === 'received' ?
                    (invoice.inv_status === 'READY' || invoice.inv_status === 'SENT') :
                    invoice.inv_status === 'PAID'
                )
            );
            
            if (clientInvoices.length === 0) {
                alert(`No eligible invoices found for ${selectedClientName} with status ${action === 'received' ? 'READY/SENT' : 'PAID'}`);
                setIsProcessingPayment(false);
                return;
            }
            
            // Prepare amounts for combination finding, sorted by inv_date ascending (oldest first).
            // Both 'received' and 'paid' use the same older-first logic when multiple combinations match.
            const amountData = clientInvoices
                .map(invoice => ({
                    id: invoice.id,
                    amount: action === 'received' ? invoice.inv_value : (invoice.pay_recruiter || 0),
                    invoice: invoice
                }))
                .sort((a, b) => {
                    const dateA = a.invoice.inv_date ? new Date(a.invoice.inv_date).getTime() : 0;
                    const dateB = b.invoice.inv_date ? new Date(b.invoice.inv_date).getTime() : 0;
                    return dateA - dateB;
                });
            
            // Find combinations that sum to target amount (prefers older entries when multiple match)
            const combinations = findSubsetSum(amountData, targetAmount);
            
            if (combinations.length === 0) {
                alert('No payment combination found');
                setIsProcessingPayment(false);
                return;
            }
            
            // Use the first valid combination found
            const selectedCombination = combinations[0];
            const newStatus = action === 'received' ? 'PAID' : 'CLOSED';
            
            // Store the pending update data and show confirmation
            setPendingInvoicesUpdate({
                action,
                selectedCombination,
                newStatus,
                targetAmount,
                clientName: selectedClientName
            });
            setShowConfirmationDialog(true);
            setIsProcessingPayment(false);
            
        } catch (error) {
            console.error(`Error preparing ${action}:`, error);
            alert(`Failed to prepare ${action}. Please try again.`);
            setIsProcessingPayment(false);
        }
    };

    const executePaymentUpdate = async () => {
        if (!pendingInvoicesUpdate) return;
        
        setIsProcessingPayment(true);
        setShowConfirmationDialog(false);
        
        try {
            const { selectedCombination, newStatus, action } = pendingInvoicesUpdate;
            const invoiceIds = selectedCombination.map(item => item.id);
            const token = sessionStorage.getItem('token');
            
            // Update each invoice — SOW invoices go READY/SENT → CLOSED (skip PAID)
            const statusById = {};
            for (const invoiceId of invoiceIds) {
                const inv = invoices.find(i => i.id === invoiceId);
                const status = action === 'received'
                    ? (isSowInvoice(inv) ? 'CLOSED' : 'PAID')
                    : 'CLOSED';
                statusById[invoiceId] = status;
                await axios.put(
                    `${process.env.REACT_APP_RYZ_SERVER}/update_client_invoice/${invoiceId}`,
                    { inv_status: status },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            }
            
            // Update local state
            setInvoices(invoices.map(invoice => 
                invoiceIds.includes(invoice.id) 
                    ? { ...invoice, inv_status: statusById[invoice.id] }
                    : invoice
            ));

            await refreshBusinessMetrics();

            const statusSummary = invoiceIds.map(id => `#${id}→${statusById[id]}`).join(', ');
            alert(`Payment ${action} recorded successfully! Updated ${invoiceIds.length} invoice(s).\n${statusSummary}`);
            
            // Reset form
            setSelectedClientName('');
            setPaymentAmount('');
            setPendingInvoicesUpdate(null);
            
        } catch (error) {
            console.error(`Error recording ${pendingInvoicesUpdate.action}:`, error);
            alert(`Failed to record ${pendingInvoicesUpdate.action}. Please try again.`);
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const cancelPaymentUpdate = () => {
        setShowConfirmationDialog(false);
        setPendingInvoicesUpdate(null);
        setIsProcessingPayment(false);
    };

    // Payable Details handlers
    const handlePayableDetails = async () => {
        setIsLoadingPayable(true);
        setShowPayableModal(true);
        setPayableDetails([]);
        setPayableTotal(0);
        
        try {
            const token = sessionStorage.getItem('token');
            
            // Filter invoices with PAID status
            const paidInvoices = invoices.filter(invoice => invoice.inv_status === 'PAID');
            
            if (paidInvoices.length === 0) {
                setPayableDetails([]);
                setIsLoadingPayable(false);
                return;
            }
            
            // Collect all invoice IDs from invoice_link fields
            const allBreakdownPromises = [];
            const invoiceClientMap = new Map(); // Map to track which client each breakdown belongs to
            
            for (const invoice of paidInvoices) {
                const invoiceIds = invoice.invoice_link
                    ? invoice.invoice_link.split(',').map(id => id.trim()).filter(id => id)
                    : [];
                
                for (const invoiceId of invoiceIds) {
                    invoiceClientMap.set(invoiceId, invoice.client_name);
                    allBreakdownPromises.push(
                        axios.get(`${process.env.REACT_APP_RYZ_SERVER}/invoice_breakdown/${invoiceId}`, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                        }).then(response => ({
                            ...response.data,
                            clientName: invoice.client_name
                        })).catch(err => {
                            console.warn(`Failed to fetch breakdown for invoice ${invoiceId}:`, err);
                            return null;
                        })
                    );
                }
            }
            
            const responses = await Promise.all(allBreakdownPromises);
            const breakdownData = responses.filter(r => r !== null);
            
            // Calculate total
            const total = breakdownData.reduce((sum, item) => sum + (item.expected_recruiter_total || 0), 0);
            
            setPayableDetails(breakdownData);
            setPayableTotal(total);
        } catch (error) {
            console.error('Error fetching payable details:', error);
            setPayableDetails([]);
        } finally {
            setIsLoadingPayable(false);
        }
    };

    const handleClosePayableModal = () => {
        setShowPayableModal(false);
        setPayableDetails([]);
        setPayableTotal(0);
        setPayableEmailAddress('');
    };

    const handleSendPayableEmail = async () => {
        if (!payableEmailAddress.trim()) {
            alert('Please enter an email address');
            return;
        }

        setIsSendingPayableEmail(true);
        
        try {
            // Build HTML table for email
            const tableRows = payableDetails.map(item => `
                <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.clientName || ''}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item["Invoice Id"] || ''}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item["Candidate Name"] || ''}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item["Recruiter Name"] || ''}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(item["Invoice Date"])}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.Hours || 0}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">$${(item.expected_recruiter_total || 0).toLocaleString()}</td>
                </tr>
            `).join('');

            const emailHtml = `
                <html>
                <body style="font-family: Arial, sans-serif;">
                    <h2>Payable Details</h2>
                    <p style="font-size: 18px; font-weight: bold; color: #2e7d32;">
                        Please find below the payment of $${payableTotal.toLocaleString()} in recognition of your  services itemized below:
                    </p>
                    <table style="border-collapse: collapse; width: 100%; margin-top: 20px;">
                        <thead>
                            <tr style="background-color: #f5f5f5;">
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Client Name</th>
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">ID</th>
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Candidate</th>
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Recruiter</th>
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Invoice Date</th>
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Hours</th>
                                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Pay Recruiter</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                        <tfoot>
                            <tr style="background-color: #e8f5e9; font-weight: bold;">
                                <td colspan="6" style="border: 1px solid #ddd; padding: 8px; text-align: right;">Total:</td>
                                <td style="border: 1px solid #ddd; padding: 8px;">$${payableTotal.toLocaleString()}</td>
                            </tr>
                        </tfoot>
                    </table>
                </body>
                </html>
            `;

            const emailPayload = {
                to_email: payableEmailAddress,
                to_name: 'Recruiter',
                subject: 'Payable Details from Rayze',
                content: emailHtml,
                from_email: process.env.REACT_APP_SENDMAIL_FROM || 'noreply@rayze.com',
                cc_email: process.env.REACT_APP_SENDMAIL_CC_CLIENT || 'jc@rayze.xyz'
            };

            // Check for test email override
            if (process.env.REACT_APP_SENDMAIL_TEST) {
                emailPayload.to_email = process.env.REACT_APP_SENDMAIL_TEST;
            }

            await axios.post(
                `${process.env.REACT_APP_RYZ_SENDMAIL}/send_html_email`,
                emailPayload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            alert('Payable details email sent successfully!');
            handleClosePayableModal();
        } catch (error) {
            console.error('Error sending payable email:', error);
            alert('Failed to send email. Please try again.');
        } finally {
            setIsSendingPayableEmail(false);
        }
    };

    const renderBreakdownRow = (invoice) => {
        if (expandedInvoiceId !== invoice.id) return null;

        // Determine header classes based on invoice status
        const getHeaderClasses = (status) => {
            //console.log('status is', status);
            switch (status) {
                case 'READY':
                case 'SENT':
                    return {
                        fromClient: 'status-red',
                        payRecruiter: 'status-red'
                    };
                case 'PAID':
                    return {
                        fromClient: 'status-green',
                        payRecruiter: 'status-red'
                    };
                case 'CLOSED':
                    return {
                        fromClient: 'status-default',
                        payRecruiter: 'status-default'
                    };
                default:
                    return {
                        fromClient: 'status-default',
                        payRecruiter: 'status-default'
                    };
            }
        };

        // Use the client invoice status from the breakdown data, or fall back to the invoice status
        const clientInvoiceStatus = invoiceBreakdown.length > 0 ? invoiceBreakdown[0].clientInvoiceStatus : invoice.inv_status;
        const headerClasses = getHeaderClasses(clientInvoiceStatus);
        //console.log('inv_status', 'invoiceBreakdown', invoice.inv_status, invoiceBreakdown);

        return (
            <tr key={`breakdown-${invoice.id}`} className="breakdown-row">
                <td colSpan="12" className="breakdown-cell">
                    <div className="breakdown-container">
                        <div className="breakdown-header">
                            <h3>Invoice Breakdown - {invoice.client_name} (Invoice #{invoice.id}) - Status: {clientInvoiceStatus}</h3>
                        </div>
                        <div className="breakdown-table-wrapper">
                            {isLoadingBreakdown ? (
                                <div className="breakdown-loading">
                                    <span>Loading breakdown data...</span>
                                </div>
                            ) : invoiceBreakdown.length > 0 ? (
                                <table className="breakdown-table">
                                    <thead>
                                        <tr>
                                            <th>Candidate</th>
                                            <th>Recruiter</th>
                                            <th>Start Period</th>
                                            <th>End Period</th>
                                            <th>Hours</th>
                                            <th className={headerClasses.fromClient}>From Client</th>
                                            <th className={headerClasses.payRecruiter}>Pay Recruiter</th>
                                            <th>Margin</th>
                                            <th>Margin/Hr</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceBreakdown.map((breakdown, index) => {
                                            const expectedClientTotal = breakdown.expected_client_total || 0;
                                            const expectedRecruiterTotal = breakdown.expected_recruiter_total || 0;
                                            const margin = expectedClientTotal - expectedRecruiterTotal;
                                            const hours = breakdown.Hours || 0;
                                            const marginPerHour = hours > 0 ? margin / hours : 0;
                                            
                                            return (
                                                <tr key={`${breakdown["Invoice Id"]}-${index}`}>
                                                    <td>{breakdown["Candidate Name"]}</td>
                                                    <td>{breakdown["Recruiter Name"]}</td>
                                                    <td>{formatDate(invoice.period_start)}</td>
                                                    <td>{formatDate(invoice.period_end)}</td>
                                                    <td>{hours}</td>
                                                    <td className={headerClasses.fromClient}>${expectedClientTotal?.toLocaleString() || '0'}</td>
                                                    <td className={headerClasses.payRecruiter}>${expectedRecruiterTotal?.toLocaleString() || '0'}</td>
                                                    <td className="margin-cell">${margin?.toLocaleString() || '0'}</td>
                                                    <td className="margin-cell">${marginPerHour?.toFixed(2) || '0.00'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="breakdown-empty">
                                    <span>No breakdown data available for this invoice</span>
                                </div>
                            )}
                        </div>
                    </div>
                </td>
            </tr>
        );
    };


    const matchesClientFilter = (invoice) => {
        if (!selectedClientName) return true;
        return invoice.client_name === selectedClientName;
    };

    const activeInvoices = invoices.filter(invoice => {
        if (invoice.inv_status === 'SUBMITTED' || invoice.inv_status === 'TEST') return false;
        if (invoice.inv_status === 'CLOSED') return false;
        return matchesClientFilter(invoice);
    });

    const closedInvoices = invoices.filter(invoice => {
        if (invoice.inv_status !== 'CLOSED') return false;
        return matchesClientFilter(invoice);
    });

    const renderInvoiceRow = (invoice) => (
        <React.Fragment key={invoice.inv_hash || invoice.id}>
            <tr
                onClick={() => handleRowClick(invoice)}
                className={`invoice-row ${expandedInvoiceId === invoice.id ? 'expanded' : ''}`}
            >
                <td>{invoice.client_name}</td>
                <td>
                    {invoice.project_name || ''}
                    {isSowInvoice(invoice) ? <span className="sow-invoice-tag"> SOW</span> : null}
                </td>
                <td>{invoice.id}</td>
                <td>
                    <span className={`status-badge ${(invoice.inv_status || '').toLowerCase()}`}>
                        {invoice.inv_status}
                    </span>
                    <span className="expand-indicator">
                        {expandedInvoiceId === invoice.id ? '▲' : '▼'}
                    </span>
                </td>
                <td>{invoice.inv_date}</td>
                <td>{invoice.period_start}</td>
                <td>{invoice.period_end}</td>
                <td className={(invoice.inv_status === 'PAID' || invoice.inv_status === 'CLOSED') ? 'amount-paid-green' : ''}>
                    USD ${(invoice.inv_value || 0).toLocaleString()}
                </td>
                <td className={invoice.inv_status === 'CLOSED' ? 'amount-closed-green' : ''}>
                    USD ${invoice.pay_recruiter ? invoice.pay_recruiter.toLocaleString() : '0'}
                </td>
                <td>
                    <div onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => handleSendEmail(invoice)}
                            className="send-button"
                            disabled={isLoadingInvoice || deletingInvoiceId === invoice.id || invoice.inv_status === 'CLOSED'}
                            title="Send invoice email"
                        >
                            {isLoadingInvoice && selectedInvoice?.inv_hash === invoice.inv_hash ? 'Loading...' : 'Send'}
                        </button>
                    </div>
                </td>
                <td>
                    <div onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => handleDeleteInvoice(invoice)}
                            className="delete-invoice-button"
                            disabled={deletingInvoiceId === invoice.id}
                            title="Delete invoice"
                            aria-label={`Delete invoice ${invoice.id}`}
                        >
                            {deletingInvoiceId === invoice.id ? '…' : 'Delete'}
                        </button>
                    </div>
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                    {(invoice.inv_hash || '').substring(0, 10)}...
                </td>
            </tr>
            {renderBreakdownRow(invoice)}
        </React.Fragment>
    );

    return (
        <div className="select-invoice-container">
            {/* Loading State */}
            {isLoading && (
                <div className="loading-message">
                    <span className="loading-spinner"></span>
                    Loading invoice data...
                </div>
            )}

            {/* Error State */}
            {timeoutError && (
                <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    {timeoutError.message}
                    <button
                        className="login-redirect-button"
                        onClick={() => navigate('/login')}
                    >
                        Go to Login
                    </button>
                </div>
            )}

            {!isLoading && !timeoutError && (
                <>
            <div className="content-header">
                <h1>Invoice Management</h1>
                <div className="header-actions">
                    <div className="payment-form">
                        <select
                            value={selectedClientName}
                            onChange={(e) => setSelectedClientName(e.target.value)}
                            className="client-dropdown"
                            disabled={isProcessingPayment}
                        >
                            <option value="">Select Client</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.name}>
                                    {client.name}
                                </option>
                            ))}
                        </select>


                        <input
                            type="number"
                            placeholder="Amount"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            className="amount-input"
                            disabled={isProcessingPayment}
                            min="0"
                            step="0.01"
                        />

                        <button
                            className="btn-received"
                            onClick={() => handlePaymentAction('received')}
                            disabled={isProcessingPayment || !selectedClientName || !paymentAmount}
                        >
                            {isProcessingPayment ? 'Processing...' : 'Received'}
                        </button>

                        <button
                            className="btn-paid"
                            onClick={() => handlePaymentAction('paid')}
                            disabled={isProcessingPayment || !selectedClientName || !paymentAmount}
                        >
                            {isProcessingPayment ? 'Processing...' : 'Paid'}
                        </button>

                        <button
                            className="btn-payable-details"
                            onClick={handlePayableDetails}
                            disabled={isLoadingPayable}
                        >
                            {isLoadingPayable ? 'Loading...' : 'Payable Details'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Business Highlights */}
            <div className="dashboard-grid">
                <div className="dashboard-card">
                    <h3>YTD Revenue</h3>
                    <div className="card-value">
                        ${businessMetrics.ytdRevenue?.toLocaleString() || '0'}
                    </div>
                    <div className="card-trend positive">
                        ${businessMetrics.lastMonthRevenue?.toLocaleString() || '0'} last month
                    </div>
                </div>
                <div className="dashboard-card">
                    <h3>YTD Profit</h3>
                    <div className="card-value">
                        ${businessMetrics.ytdProfit?.toLocaleString() || '0'}
                    </div>
                    <div className="card-trend positive">
                        ${businessMetrics.lastMonthProfit?.toLocaleString() || '0'} last month
                    </div>
                </div>
                <div className="dashboard-card">
                    <h3>YTD Margin</h3>
                    <div className="card-value">
                        {businessMetrics.ytdMargin?.toFixed(1) || '0'}%
                    </div>
                    <div className="card-trend neutral">
                        Profit / Revenue
                    </div>
                </div>
                <div className="dashboard-card">
                    <h3>Total Payment Outstanding</h3>
                    <div className="card-value">
                        ${businessMetrics.totalPaymentOutstanding?.toLocaleString() || '0'}
                    </div>
                    <div className="card-trend neutral">
                        To recruiters
                    </div>
                </div>
                <div className="dashboard-card">
                    <h3>Total Revenue Outstanding</h3>
                    <div className="card-value">
                        ${businessMetrics.totalRevenueOutstanding?.toLocaleString() || '0'}
                    </div>
                    <div className="card-trend neutral">
                        From clients
                    </div>
                </div>
            </div>

            {/* Invoices Table Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2 style={{ color: 'var(--theme-color)' }}>Client Invoices</h2>
                </div>
                <div className="table-container">
                    <div className="table-scroll-wrapper">
                        <table>
                        <thead>
                            <tr>
                                <th>Client Name</th>
                                <th>Project Name</th>
                                <th>ID</th>
                                <th>Status</th>
                                <th>Invoice Date</th>
                                <th>Period Start</th>
                                <th>Period End</th>
                                <th>Client Invoice</th>
                                <th>Pay Recruiter</th>
                                <th>Send</th>
                                <th>Delete</th>
                                <th>Invoice Hash</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeInvoices.map(renderInvoiceRow)}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>

            {/* Closed Invoices — fold-down (collapsed by default). Still counted in YTD metrics. */}
            <div className="dashboard-section closed-invoices-section">
                <button
                    type="button"
                    className="closed-invoices-toggle"
                    onClick={() => setClosedInvoicesOpen(o => !o)}
                    aria-expanded={closedInvoicesOpen}
                >
                    <span>
                        Closed Invoices
                        <span className="closed-invoices-count">{closedInvoices.length}</span>
                    </span>
                    <span className="closed-invoices-chevron">{closedInvoicesOpen ? '▲' : '▼'}</span>
                </button>
                {closedInvoicesOpen && (
                    <div className="table-container closed-invoices-body">
                        <div className="table-scroll-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Client Name</th>
                                        <th>Project Name</th>
                                        <th>ID</th>
                                        <th>Status</th>
                                        <th>Invoice Date</th>
                                        <th>Period Start</th>
                                        <th>Period End</th>
                                        <th>Client Invoice</th>
                                        <th>Pay Recruiter</th>
                                        <th>Send</th>
                                        <th>Delete</th>
                                        <th>Invoice Hash</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {closedInvoices.length === 0 ? (
                                        <tr>
                                            <td colSpan={12} style={{ textAlign: 'center', opacity: 0.6 }}>
                                                No closed invoices
                                            </td>
                                        </tr>
                                    ) : (
                                        closedInvoices.map(renderInvoiceRow)
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Email Modal */}
            {showEmailModal && (
                <div className="modal-overlay">
                    <div className="modal-content email-modal">
                        <div className="modal-header">
                            <h2>Send Invoice Email</h2>
                            <button 
                                className="close-button" 
                                onClick={handleCloseModal}
                                disabled={isSendingEmail}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="email-form">
                                <label htmlFor="email-address">Email Address:</label>
                                <input
                                    id="email-address"
                                    type="email"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    className="email-input"
                                    disabled={isSendingEmail}
                                />
                            </div>
                            <div className="invoice-preview">
                                <h3>Invoice Preview:</h3>
                                <div 
                                    className="invoice-content"
                                    dangerouslySetInnerHTML={{ __html: invoiceHtml }}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-button cancel"
                                onClick={handleCloseModal}
                                disabled={isSendingEmail}
                            >
                                Cancel
                            </button>
                            <button 
                                className="modal-button send-email"
                                onClick={handleSendEmailSubmit}
                                disabled={isSendingEmail || !emailAddress.trim()}
                            >
                                {isSendingEmail ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Confirmation Dialog */}
            {showConfirmationDialog && pendingInvoicesUpdate && (
                <div className="modal-overlay">
                    <div className="modal-content confirmation-modal">
                        <div className="modal-header">
                            <h2>Confirm Payment Update</h2>
                            <button 
                                className="close-button" 
                                onClick={cancelPaymentUpdate}
                                disabled={isProcessingPayment}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="confirmation-summary">
                                <h3>Payment Details:</h3>
                                <p><strong>Client:</strong> {pendingInvoicesUpdate.clientName}</p>
                                <p><strong>Action:</strong> Mark as {pendingInvoicesUpdate.action === 'received' ? 'Payment Received' : 'Payment Sent'}</p>
                                <p><strong>Amount:</strong> ${pendingInvoicesUpdate.targetAmount.toLocaleString()}</p>
                                <p><strong>New Status:</strong> {pendingInvoicesUpdate.action === 'received'
                                    ? (pendingInvoicesUpdate.selectedCombination.some(item => isSowInvoice(item.invoice))
                                        ? 'CLOSED for SOW / PAID for others'
                                        : 'PAID')
                                    : 'CLOSED'}
                                </p>
                            </div>
                            
                            <div className="invoices-to-update">
                                <h3>Invoices to be updated ({pendingInvoicesUpdate.selectedCombination.length}):</h3>
                                <div className="invoice-list">
                                    {pendingInvoicesUpdate.selectedCombination.map((item) => {
                                        const nextStatus = pendingInvoicesUpdate.action === 'received'
                                            ? (isSowInvoice(item.invoice) ? 'CLOSED' : 'PAID')
                                            : 'CLOSED';
                                        return (
                                        <div key={item.id} className="invoice-item">
                                            <span className="invoice-id">
                                                Invoice #{item.id}
                                                {isSowInvoice(item.invoice) ? ' (SOW)' : ''}
                                                {' → '}{nextStatus}
                                            </span>
                                            <span className="invoice-amount">${item.amount.toLocaleString()}</span>
                                            <span className="invoice-period">
                                                {item.invoice.period_start} to {item.invoice.period_end}
                                            </span>
                                        </div>
                                        );
                                    })}
                                </div>
                                <div className="total-amount">
                                    <strong>Total: ${pendingInvoicesUpdate.selectedCombination.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}</strong>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-button cancel"
                                onClick={cancelPaymentUpdate}
                                disabled={isProcessingPayment}
                            >
                                Cancel
                            </button>
                            <button 
                                className="modal-button confirm"
                                onClick={executePaymentUpdate}
                                disabled={isProcessingPayment}
                            >
                                {isProcessingPayment ? 'Processing...' : 'Confirm Update'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payable Details Modal */}
            {showPayableModal && (
                <div className="modal-overlay">
                    <div className="modal-content email-modal">
                        <div className="modal-header">
                            <h2>Payable Details</h2>
                            <button 
                                className="close-button" 
                                onClick={handleClosePayableModal}
                                disabled={isSendingPayableEmail}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="payable-total-statement">
                                <p>The total outstanding payment based on the services below is <strong>${payableTotal.toLocaleString()}</strong></p>
                            </div>
                            <div className="email-form">
                                <label htmlFor="payable-email-address">Send To (Email Address):</label>
                                <input
                                    id="payable-email-address"
                                    type="email"
                                    value={payableEmailAddress}
                                    onChange={(e) => setPayableEmailAddress(e.target.value)}
                                    className="email-input payable-email-input"
                                    placeholder="Enter recipient email address"
                                    disabled={isSendingPayableEmail}
                                />
                            </div>
                            <div className="invoice-preview">
                                <h3>Payment Details:</h3>
                                <div className="invoice-content">
                                    {isLoadingPayable ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            Loading payable details...
                                        </div>
                                    ) : payableDetails.length > 0 ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th>Client Name</th>
                                                    <th>ID</th>
                                                    <th>Candidate</th>
                                                    <th>Recruiter</th>
                                                    <th>Invoice Date</th>
                                                    <th>Hours</th>
                                                    <th>Pay Recruiter</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payableDetails.map((item, index) => (
                                                        <tr key={`payable-${item["Invoice Id"]}-${index}`}>
                                                            <td>{item.clientName || ''}</td>
                                                            <td>{item["Invoice Id"] || ''}</td>
                                                            <td>{item["Candidate Name"] || ''}</td>
                                                            <td>{item["Recruiter Name"] || ''}</td>
                                                            <td>{formatDate(item["Invoice Date"])}</td>
                                                            <td>{item.Hours || 0}</td>
                                                            <td>${(item.expected_recruiter_total || 0).toLocaleString()}</td>
                                                        </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ backgroundColor: '#e8f5e9', fontWeight: 'bold' }}>
                                                    <td colSpan="6" style={{ textAlign: 'right', padding: '10px' }}>Total:</td>
                                                    <td style={{ padding: '10px' }}>${payableTotal.toLocaleString()}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No payable details found for PAID invoices
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-button cancel"
                                onClick={handleClosePayableModal}
                                disabled={isSendingPayableEmail}
                            >
                                Cancel
                            </button>
                            <button 
                                className="modal-button send-email"
                                onClick={handleSendPayableEmail}
                                disabled={isSendingPayableEmail || !payableEmailAddress.trim() || payableDetails.length === 0}
                            >
                                {isSendingPayableEmail ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </>
            )}
        </div>
    );
};

export default SelectInvoicePage;
